import {useContext, useEffect, useState} from "react";
import {useRecoilValue} from "recoil";
import axios from "axios";
import {raDetailState} from "../state";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {IAncillaryDiscountRange, resolveGovernanceRange} from "../../create-ra/ra-filling/ancillaries-info/discount";

/**
 * Range sconto banco (min/max) dalla governance desk-discount per il flusso post-firma sul
 * dettaglio RA (LPP-21950, feedback INT).
 *
 * A differenza di {@code useDeskDiscountRange} (che legge lo state di create-ra), questo hook
 * ricava lo scope dal {@code raDetailState}: location del RA + profili dell'operatore loggato.
 * Dealer e legalEntity sono usati solo come filtro aggiuntivo quando disponibili, così il range
 * mostrato al banco e post-firma coincide (stessa governance location x profili).
 *
 * Restituisce null quando non trova config applicabile → AncillaryDiscountSelect usa il fallback.
 */
const useDeskDiscountRangeForRa = (): IAncillaryDiscountRange | null => {
	const raDetail: any = useRecoilValue(raDetailState);
	const {globalData} = useContext(GlobalContext);
	const [range, setRange] = useState<IAncillaryDiscountRange | null>(null);

	const locationCode: string | undefined =
		raDetail?.generalInfo?.dmsIdentificationCode?.code
		?? raDetail?.generalInfo?.pickupLocation?.code
		?? raDetail?.dmsCode?.code;
	const dealer: string | undefined = raDetail?.dealer;
	const legalEntity: string | undefined = raDetail?.idTenant;
	const profiles: any[] = globalData?.user?.profiles;

	useEffect(() => {
		const fetchGovernance = async () => {
			try {
				if (!locationCode || !profiles?.length) {
					setRange(null);
					return;
				}

				const lppProfileIds = profiles.map((p: any) => p.id).filter(Boolean);

				const params = new URLSearchParams();
				params.append("locationCodes", locationCode);
				if (dealer) {
					params.append("dealers", dealer);
				}
				if (legalEntity) {
					params.append("legalEntities", legalEntity);
				}
				lppProfileIds.forEach((id: number) => params.append("lppProfileIds", String(id)));
				params.append("size", "500");
				params.append("page", "0");

				const response = await axios.get(
					`/pricing/desk-discount-governance/paged/filter?${params.toString()}`
				);

				const records = response.data?.content ?? [];
				const now = new Date().toISOString();

				const activeRecords = records.filter((r: any) =>
					r.active === true &&
					r.validityDateFrom && r.validityDateFrom <= now &&
					(!r.validityDateTo || r.validityDateTo >= now)
				);

				if (activeRecords.length === 0) {
					setRange(null);
					return;
				}

				// Più profili -> range unione (min più basso, max più alto), coerente col backend
				// (resolveValidatedPercentageForProfiles). discountMin/Max in centesimi di punto.
				const minValue = Math.min(...activeRecords.map((r: any) => r.discountMin));
				const maxValue = Math.max(...activeRecords.map((r: any) => r.discountMax));

				// LPP-24845 (requisito aggiornato): passo 5. La conversione centesimi -> punti percentuali
				// offre SOLO i multipli di 5 interni al range (min in su, max in giù al multiplo di 5),
				// così ogni voce della tendina è una percentuale che PRC accetta. Dettaglio in resolveGovernanceRange.
				const resolved = resolveGovernanceRange(minValue, maxValue);
				if (!resolved) {
					// Config attiva presente ma nessuna percentuale intera ammessa (es. 250-290 =
					// 2,5-2,9%): il controllo ricade sul fallback di resolveDiscountRange, che può
					// offrire valori fuori governance. Caso teorico, tracciato per essere rilevabile.
					console.warn(
						`Desk discount governance range [${minValue}, ${maxValue}] (centesimi di punto) `
						+ `non espone alcuna percentuale intera: la tendina sconto usa il fallback.`
					);
				}
				setRange(resolved);
			} catch (e) {
				setRange(null);
			}
		};

		fetchGovernance();
	}, [locationCode, dealer, legalEntity, profiles]);

	return range;
};

export default useDeskDiscountRangeForRa;
